/**
 * Rolagem horizontal que se anuncia.
 *
 * Aba, tabela larga e quadro de etapas rolam de lado dentro do próprio
 * contêiner — é assim que cabem no celular sem a página inteira rolar. O defeito
 * era rolar CALADO: a última aba terminava cortada na borda ("Contas financ…"),
 * a coluna "Ganho" do CRM sumia fora do quadro, e nada dizia que havia mais.
 *
 * Este gancho mede se há conteúdo antes e depois do que está à vista; quem o
 * usa esmaece a borda correspondente (`mascaraDaRolagem`) e, com mouse, põe as
 * setas. A máscara é de opacidade, não de cor: funciona sobre qualquer fundo,
 * no tema claro e no escuro.
 *
 * O atributo `data-rolagem="horizontal"` marca o contêiner que rola COM aviso —
 * é por ele que a varredura de margens do e2e distingue rolagem de projeto de
 * texto cortado.
 */

import { useCallback, useEffect, useState } from 'react';
import type { CSSProperties, RefObject } from 'react';

export interface EstadoDaRolagem {
    /** Há conteúdo escondido à esquerda. */
    antes: boolean;
    /** Há conteúdo escondido à direita. */
    depois: boolean;
    /** Mouse ou trackpad: só aí as setas fazem sentido — no toque, arrasta-se. */
    ponteiroFino: boolean;
    /** Rola quase uma tela na direção pedida. */
    rolar: (sentido: 1 | -1) => void;
}

const consulta = (media: string): boolean =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(media).matches;

/**
 * @param conteudo Muda quando o conteúdo muda (número de abas, de linhas): é o
 *   que faz o gancho medir de novo e observar os filhos que chegaram.
 */
export function useRolagemHorizontal(ref: RefObject<HTMLElement | null>, conteudo?: unknown): EstadoDaRolagem {
    const [antes, setAntes] = useState(false);
    const [depois, setDepois] = useState(false);
    const [ponteiroFino] = useState(() => consulta('(hover: hover) and (pointer: fine)'));

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const medir = () => {
            /* 1 px de folga: arredondamento de layout não é conteúdo escondido. */
            setAntes(el.scrollLeft > 1);
            setDepois(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
        };
        medir();
        el.addEventListener('scroll', medir, { passive: true });
        /* O contêiner muda com a janela; o conteúdo muda com a fonte que termina
           de carregar ou com a linha que chega. Os dois alteram o que cabe. */
        const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(medir);
        if (ro) {
            ro.observe(el);
            for (const filho of el.children) ro.observe(filho);
        }
        return () => {
            el.removeEventListener('scroll', medir);
            ro?.disconnect();
        };
    }, [ref, conteudo]);

    const rolar = useCallback((sentido: 1 | -1) => {
        const el = ref.current;
        if (!el || typeof el.scrollBy !== 'function') return;
        el.scrollBy({
            left: sentido * Math.max(120, el.clientWidth * 0.8),
            behavior: consulta('(prefers-reduced-motion: reduce)') ? 'auto' : 'smooth',
        });
    }, [ref]);

    return { antes, depois, ponteiroFino, rolar };
}

/** Esmaece a borda onde há mais conteúdo. Sem nada escondido, não mascara nada. */
export function mascaraDaRolagem({ antes, depois }: Pick<EstadoDaRolagem, 'antes' | 'depois'>, faixa = 32): CSSProperties {
    if (!antes && !depois) return {};
    const imagem = `linear-gradient(to right, ${antes ? 'transparent' : '#000'} 0, #000 ${antes ? faixa : 0}px, #000 calc(100% - ${depois ? faixa : 0}px), ${depois ? 'transparent' : '#000'} 100%)`;
    return { maskImage: imagem, WebkitMaskImage: imagem };
}

/**
 * Traz um item para dentro do contêiner que rola, sem mexer na rolagem da
 * página. `scrollIntoView` rolaria também o `<main>` na vertical — a aba ativa
 * apareceria e o título da tela sumiria.
 */
export function mostrarNaRolagem(contêiner: HTMLElement, item: HTMLElement, folga = 32): void {
    const inicio = item.offsetLeft - folga;
    const fim = item.offsetLeft + item.offsetWidth + folga - contêiner.clientWidth;
    if (contêiner.scrollLeft > inicio) contêiner.scrollLeft = Math.max(0, inicio);
    else if (contêiner.scrollLeft < fim) contêiner.scrollLeft = fim;
}
